import { ENTERPRISE_CUSTOMER_ID_PREFIX } from '@/utils/constants'
import { isPaywallEnabled } from '@/utils/featureFlags'

export const PLANS = [
  {
    name: 'Basic',
    value: 'basic',
    monthlyPrice: 25,
    yearlyPrice: 250,
    maxProjectsCount: 3,
    description: 'Perfect for individuals or small teams just getting started',
    prices: {
      monthly: window.location.hostname === 'app.qlerify.com'
        ? 'price_1RD5yXALrMdFlaXPltMstvIv'
        : 'price_1QRUjHALrMdFlaXPttQI8y0K',
      yearly: window.location.hostname === 'app.qlerify.com'
        ? 'price_1RD5yXALrMdFlaXPQ2vuB0Eo'
        : 'price_1QRUjHALrMdFlaXPKdK19nDM'
    },
    features: [
      '3 Projects',
      '5 Workflows per project',
      '10GB of storage for documents and attachments',
      'Unlimited visitors (1-click access, no sign-in)',
      'Advanced AI-powered features',
      'Unlimited amount of AI prompts'
    ]
  },
  {
    name: 'Pro',
    value: 'pro',
    monthlyPrice: 40,
    yearlyPrice: 400,
    maxProjectsCount: Infinity,
    description: 'Ideal for growing teams and businesses',
    prices: {
      monthly: window.location.hostname === 'app.qlerify.com'
        ? 'price_1RD5yRALrMdFlaXP5IaW68OL'
        : 'price_1QRUkfALrMdFlaXPmR5WZ9G9',
      yearly: window.location.hostname === 'app.qlerify.com'
        ? 'price_1RD5yRALrMdFlaXPSaRp1XjS'
        : 'price_1QRUkfALrMdFlaXPOT0g83yb'
    },
    features: [
      'No credit card required. Free for 4 weeks, cancel anytime.',
      'Unlimited number of projects',
      'Unlimited number of workflows',
      'Unlimited number of documents and attachments',
      'Priority email support',
      'On-boarding and training sessions',
      '99.9% uptime guarantee'
    ]
  }
]

export const ENTERPRISE_PLAN = {
  name: 'Enterprise',
  value: 'enterprise',
  maxProjectsCount: Infinity,
  description: 'Custom solutions for large organizations',
  features: [
    'Dedicated Account Manager',
    'Custom integrations',
    'Advanced and customized security features',
    'Custom training and workshops',
    'Phone and video support',
    'SSO (Single sign-on) integration',
    'Access to early feature releases',
    'Custom continued education and webinars'
  ]
}

export function getPlanByPriceId (priceId) {
  // Platform mode: no paid tiers — every plan lookup resolves to the unlimited
  // plan so project/workflow limits never gate the user.
  if (!isPaywallEnabled()) {
    return ENTERPRISE_PLAN
  }
  return PLANS.find((plan) => [plan.prices.monthly, plan.prices.yearly].includes(priceId))
}

export function getSubscriptionTypeByPriceId (priceId) {
  const isMonthly = PLANS.findIndex((plan) => [plan.prices.monthly].includes(priceId)) !== -1
  const isYearly = PLANS.findIndex((plan) => [plan.prices.yearly].includes(priceId)) !== -1

  return isMonthly ? 'monthly' : isYearly ? 'yearly' : null
}

export function getSubscriptionAccessType (activeSubscription) {
  if (!isPaywallEnabled()) {
    return 'FULL'
  }

  if (!activeSubscription || activeSubscription.subscriptionDetails?.status === 'unpaid') {
    return 'NO_ACCESS'
  }

  const isEnterpriseUser = !!activeSubscription &&
    activeSubscription?.subscriptionDetails?.subscriptionId?.startsWith(ENTERPRISE_CUSTOMER_ID_PREFIX)
  const isSubscriptionActive = ['active', 'trialing'].includes(activeSubscription?.subscriptionDetails?.status)
  const lastSubscriptionDetails = activeSubscription?.lastSubscriptionDetails

  return isSubscriptionActive ? 'FULL' : isEnterpriseUser || lastSubscriptionDetails ? 'LIMITED' : 'NO_ACCESS'
}

export const SUBSCRIPTION_STATE = {
  ACTIVE: 'ACTIVE',
  TRIALING: 'TRIALING',
  PAST_DUE: 'PAST_DUE',
  UNPAID: 'UNPAID',
  INCOMPLETE: 'INCOMPLETE',
  CANCELED: 'CANCELED',
  NONE: 'NONE'
}

export function getSubscriptionState (activeSubscription) {
  if (!activeSubscription) {
    return SUBSCRIPTION_STATE.NONE
  }

  const status = activeSubscription.subscriptionDetails?.status ||
    activeSubscription.lastSubscriptionDetails?.status

  switch (status) {
    case 'active':
      return SUBSCRIPTION_STATE.ACTIVE
    case 'trialing':
      return SUBSCRIPTION_STATE.TRIALING
    case 'past_due':
      return SUBSCRIPTION_STATE.PAST_DUE
    case 'unpaid':
      return SUBSCRIPTION_STATE.UNPAID
    case 'incomplete':
      return SUBSCRIPTION_STATE.INCOMPLETE
    case 'canceled':
    case 'incomplete_expired':
      return SUBSCRIPTION_STATE.CANCELED
    default:
      return SUBSCRIPTION_STATE.NONE
  }
}

export function isRecoverableState (subscriptionState) {
  return [
    SUBSCRIPTION_STATE.PAST_DUE,
    SUBSCRIPTION_STATE.UNPAID,
    SUBSCRIPTION_STATE.INCOMPLETE
  ].includes(subscriptionState)
}

export function getScheduledQuantity (activeSubscription) {
  return activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.quantity
}

export function hasCardOnFile (activeSubscription) {
  const lastFour = activeSubscription?.subscriptionDetails?.cardDetails?.lastFour ||
    activeSubscription?.lastSubscriptionDetails?.cardDetails?.lastFour

  return !!lastFour && lastFour !== 'N/A'
}
